/**
 * MobileEmploymentPromisesAddModal
 *
 * MUI full-screen Dialog replacement for EmploymentPromisesAddModal (PrimeReact Dialog).
 * Follows the same pattern as MobileAddContractModal.
 *
 * Components from MUI: Dialog, AppBar, Toolbar, IconButton, Typography, Autocomplete,
 *   TextField, Button, Box, Chip, Slide
 */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// MUI Components
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Box,
  Chip,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Redux
import { getCompanies } from "../../../components/collabs/collabsSlice";
import {
  addEmploymentPromise,
  getEmploymentPromises,
} from "../../../components/employmentPromises/employmentPromisesSlice";

// Shared utilities
import Langs from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": {
    fontSize: "16px",
    transition: TRANSITION_SMOOTH,
  },
  "& input::placeholder": {
    opacity: 0.45,
  },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const titleOptions = [
  { label: "M.", value: "M." },
  { label: "Mme", value: "Mme" },
  { label: "Indéfini", value: "Indéfini" },
];

const progressionOptions = [
  { label: "Attente d'envoi", value: "Attente d'envoi" },
  { label: "Envoyé", value: "Envoyé" },
  { label: "Reçu", value: "Reçu" },
];

const statusOptions = [
  { label: "OK", value: "OK" },
  { label: "KO", value: "KO" },
];

const progressionChipColor = (value) => {
  switch (value) {
    case "Attente d'envoi":
      return "warning";
    case "Envoyé":
      return "info";
    case "Reçu":
      return "success";
    default:
      return "default";
  }
};

const statusChipColor = (value) => {
  switch (value) {
    case "OK":
      return "success";
    case "KO":
      return "error";
    default:
      return "default";
  }
};

export default function MobileEmploymentPromisesAddModal(props) {
  const dispatch = useDispatch();
  const companies = useSelector((state) => state.collabs.companies);

  const [selectedItem, setSelectedItem] = useState({});

  useEffect(() => {
    if (props.visible) {
      dispatch(getCompanies());
    }
  }, [props.visible]);

  const handleClose = () => {
    props.setVisible(false);
    setSelectedItem({});
  };

  const handleSave = () => {
    const ep = { ...selectedItem };
    if (ep && ep.title && typeof ep.title === "string" && ep.title !== "") {
      ep.start_date = ep.start_date
        ? moment(ep.start_date).format("YYYY-MM-DD")
        : null;
      ep.request_date = ep.request_date
        ? moment(ep.request_date).format("YYYY-MM-DD")
        : null;
      ep.sent_date = ep.sent_date
        ? moment(ep.sent_date).format("YYYY-MM-DD")
        : null;
      dispatch(addEmploymentPromise({ employment_promise: ep })).then(
        (actionResponse) => {
          if (addEmploymentPromise.fulfilled.match(actionResponse)) {
            if (
              actionResponse.payload.responseData &&
              actionResponse.payload.responseData.status
            ) {
              dispatch(getEmploymentPromises());
              handleClose();
            }
          }
        }
      );
    } else {
      showErrorToast(
        `Veuillez remplir les champs obligatoires "titre", "nom", et "prenom" .`
      );
    }
  };

  const updateField = (key, value) => {
    setSelectedItem((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog
      fullScreen
      open={!!props.visible}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "var(--surface-ground, #f4f6f8)",
        },
      }}
    >
      {/* Top AppBar */}
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{
              transition: TRANSITION_SMOOTH,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.06)",
                transform: "scale(1.08)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              ml: 1.5,
              flex: 1,
              fontWeight: 600,
              fontSize: "1.15rem",
              letterSpacing: "-0.02em",
            }}
            variant="h6"
          >
            <Langs str="ADD_EMPLOYMENT_PROMISE" />
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Form Content */}
      <Box
        sx={{
          p: 2.5,
          pb: 14,
          overflowY: "auto",
          flex: 1,
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
        }}
      >
        <Box
          sx={{
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.45)",
            boxShadow: "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
            p: 2.5,
            transition: TRANSITION_SMOOTH,
          }}
        >
          {/* Company */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="COMPANY" />
          </Typography>
          <Autocomplete
            options={companies || []}
            getOptionLabel={(opt) => opt.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={
              (companies || []).find(
                (o) => o.value === selectedItem.id_company
              ) || null
            }
            onChange={(_, newVal) =>
              updateField("id_company", newVal ? newVal.value : null)
            }
            slotProps={{ popper: { sx: { zIndex: 1500 } } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionner une entreprise"
                size="small"
                sx={glassmorphicTextFieldSx}
              />
            )}
            fullWidth
          />

          {/* Title */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="TITLE" /> *
          </Typography>
          <Autocomplete
            options={titleOptions}
            getOptionLabel={(opt) => opt.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={
              titleOptions.find((o) => o.value === selectedItem.title) || null
            }
            onChange={(_, newVal) =>
              updateField("title", newVal ? newVal.value : null)
            }
            slotProps={{ popper: { sx: { zIndex: 1500 } } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionner un titre"
                size="small"
                sx={glassmorphicTextFieldSx}
              />
            )}
            fullWidth
          />

          {/* Name */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="NAME" /> *
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={selectedItem.name || ""}
            onChange={(e) => updateField("name", e.target.value)}
            placeholder="Nom"
            sx={glassmorphicTextFieldSx}
          />

          {/* Family Name */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="FAMILY_NAME" /> *
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={selectedItem.family_name || ""}
            onChange={(e) => updateField("family_name", e.target.value)}
            placeholder="Prénom"
            sx={glassmorphicTextFieldSx}
          />

          {/* Email */}
          <Typography variant="body2" sx={fieldLabelSx}>
            Email
          </Typography>
          <TextField
            size="small"
            fullWidth
            type="email"
            value={selectedItem.email || ""}
            onChange={(e) => updateField("email", e.target.value)}
            placeholder="Email"
            sx={glassmorphicTextFieldSx}
          />

          {/* Progression */}
          <Typography variant="body2" sx={fieldLabelSx}>
            Progression
          </Typography>
          <Autocomplete
            options={progressionOptions}
            getOptionLabel={(opt) => opt.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={
              progressionOptions.find(
                (o) => o.value === selectedItem.progression
              ) || null
            }
            onChange={(_, newVal) =>
              updateField("progression", newVal ? newVal.value : null)
            }
            slotProps={{ popper: { sx: { zIndex: 1500 } } }}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <Chip
                  label={option.label}
                  color={progressionChipColor(option.value)}
                  size="small"
                  sx={{ borderRadius: "8px" }}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionner une progression"
                size="small"
                sx={glassmorphicTextFieldSx}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedItem.progression ? (
                    <Chip
                      label={selectedItem.progression}
                      color={progressionChipColor(selectedItem.progression)}
                      size="small"
                      sx={{ mr: 0.5, borderRadius: "8px" }}
                    />
                  ) : null,
                }}
              />
            )}
            fullWidth
          />

          {/* Status */}
          <Typography variant="body2" sx={fieldLabelSx}>
            Status
          </Typography>
          <Autocomplete
            options={statusOptions}
            getOptionLabel={(opt) => opt.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={
              statusOptions.find((o) => o.value === selectedItem.status) || null
            }
            onChange={(_, newVal) =>
              updateField("status", newVal ? newVal.value : null)
            }
            slotProps={{ popper: { sx: { zIndex: 1500 } } }}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <Chip
                  label={option.label}
                  color={statusChipColor(option.value)}
                  size="small"
                  sx={{ borderRadius: "8px" }}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionner un statut"
                size="small"
                sx={glassmorphicTextFieldSx}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedItem.status ? (
                    <Chip
                      label={selectedItem.status}
                      color={statusChipColor(selectedItem.status)}
                      size="small"
                      sx={{ mr: 0.5, borderRadius: "8px" }}
                    />
                  ) : null,
                }}
              />
            )}
            fullWidth
          />

          {/* Start Date */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="START_DATE" />
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={
              selectedItem.start_date
                ? moment(selectedItem.start_date).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              updateField(
                "start_date",
                e.target.value ? e.target.value : null
              )
            }
            InputLabelProps={{ shrink: true }}
            sx={glassmorphicTextFieldSx}
          />

          {/* Sent Date */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="SENT_DATE" />
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={
              selectedItem.sent_date
                ? moment(selectedItem.sent_date).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              updateField(
                "sent_date",
                e.target.value ? e.target.value : null
              )
            }
            InputLabelProps={{ shrink: true }}
            sx={glassmorphicTextFieldSx}
          />

          {/* Request Date */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="REQUEST_DATE" />
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={
              selectedItem.request_date
                ? moment(selectedItem.request_date).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              updateField(
                "request_date",
                e.target.value ? e.target.value : null
              )
            }
            InputLabelProps={{ shrink: true }}
            sx={glassmorphicTextFieldSx}
          />
        </Box>
      </Box>

      {/* Sticky bottom button */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2.5,
          bgcolor: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
          borderTop: "1px solid rgba(255,255,255,0.5)",
          zIndex: 1,
        }}
      >
        <Button
          variant="contained"
          color="warning"
          fullWidth
          onClick={handleSave}
          sx={{
            minHeight: 52,
            borderRadius: BORDER_RADIUS_BUTTON,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1.05rem",
            letterSpacing: "-0.01em",
            boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
          }}
        >
          <Langs str="ADD" />
        </Button>
      </Box>
    </Dialog>
  );
}
